// .scss, not .css: see Fields/fileFiled/uploader.js — a plain .css import is
// never injected in the production bundle.
import './phone.scss'
import geoCountry from '@/common/FF/Fields/geoCountry'

/**
 * Phone field widget — searchable country dropdown + national number.
 *
 * intl-tel-input ships modern JS that Webpack 4 cannot parse, so its prebuilt
 * files are copied to /exo_editor/vendor/intl-tel-input by `npm run deploy` and
 * loaded with a script tag, only on pages that have a phone field. The build
 * WITH utils is used: turning "06 12345678" + NL into +31612345678 needs each
 * country's trunk-prefix rules (NL drops the 0, IT keeps it).
 *
 * The visible input holds the national part; the value that is submitted is the
 * full E.164 number in a hidden input that takes over the field's name. It is
 * kept in sync on every change because ExoForm submits with the native
 * form.submit(), which fires no submit event to hook.
 *
 * Without the widget (script blocked, no JS) the plain tel input still posts
 * whatever was typed, and the server asks for a +country number.
 */
const BASE = '/exo_editor/vendor/intl-tel-input'
let loading = null

function load() {
    if (window.intlTelInput) return Promise.resolve(window.intlTelInput)
    if (!loading) {
        loading = new Promise((resolve, reject) => {
            const css = document.createElement('link')
            css.rel = 'stylesheet'
            css.href = BASE + '/css/intlTelInput.min.css'
            document.head.appendChild(css)

            const script = document.createElement('script')
            script.src = BASE + '/js/intlTelInputWithUtils.min.js'
            script.onload = () => resolve(window.intlTelInput)
            script.onerror = reject
            document.head.appendChild(script)
        })
    }
    return loading
}

export default {
    init() {
        const inputs = document.querySelectorAll('input[data-exo-phone]')
        if (!inputs.length) return
        load()
            .then(intlTelInput => inputs.forEach(el => this.mount(intlTelInput, el)))
            .catch(() => console.warn('Exolog phone: intl-tel-input failed to load, plain input kept'))
    },

    mount(intlTelInput, input) {
        if (input.dataset.exoPhoneMounted || !input.name) return
        input.dataset.exoPhoneMounted = '1'

        let cfg = {}
        try { cfg = JSON.parse(input.dataset.exoPhone || '{}') } catch (e) { /* defaults */ }

        // The dropdown starts on: the country of a number already in the field
        // (intl-tel-input reads it from the value), else the visitor's country by
        // IP, else the field's default country.
        const fallback = cfg.country || 'nl'
        const iti = intlTelInput(input, {
            initialCountry: input.value.trim() ? fallback : 'auto',
            geoIpLookup: success => geoCountry().then(code => success(code || fallback)),
            separateDialCode: true,
            countrySearch: true,
        })

        // The country search box is type="search"; site stylesheets style
        // input[type=text], so as text it wears the site's own input look and
        // theme colour, the same as the passport field's search box.
        const countrySearch = (input.closest('.iti') || document).querySelector('.iti__search-input')
        if (countrySearch) countrySearch.type = 'text'

        // intl-tel-input puts its country picker (with that search box) BEFORE the
        // number input. Site scripts that take a form group's first input — a
        // floating label that rises when "the" input has a value, as on mistergold —
        // then watch the empty search box and never raise the label. The picker is
        // absolutely positioned, so moving it after the input changes nothing on
        // screen and makes the number the group's first input again.
        const countryPicker = (input.closest('.iti') || document).querySelector('.iti__country-container')
        if (countryPicker && (input.compareDocumentPosition(countryPicker) & Node.DOCUMENT_POSITION_PRECEDING)) {
            input.after(countryPicker)
        }

        const hidden = document.createElement('input')
        hidden.type = 'hidden'
        hidden.name = input.name
        input.removeAttribute('name')
        input.after(hidden)

        const sync = () => { hidden.value = input.value.trim() ? iti.getNumber() : '' }
        input.addEventListener('input', sync)
        input.addEventListener('countrychange', sync)
        sync()

        if (cfg.verify) this.mountVerify(iti, input, cfg.verify)
    },

    /**
     * "Verify by SMS", laid out like the e-mail code screen:
     *
     *   valid number, not verified   → "Send code" inside the field, at its end
     *   code sent                    → six digit boxes under the field, "Paste the
     *                                  code", "Request new code" (or its countdown)
     *   verified (now, or already on
     *   the user's profile)          → code part gone, green ✓ in the field
     *
     * The submit is held by a visually hidden `required` input that carries a
     * custom validity error while the number is valid but not verified: ExoForm's
     * submit gate asks every visible required field for checkValidity(), so it
     * disables the button on its own and nothing here touches the button. The
     * server checks the same thing on submit (PhoneNumber rule); this is the
     * courtesy, not the lock.
     */
    mountVerify(iti, input, v) {
        const L = v.labels || {}
        const DIGITS = 6
        const box = document.createElement('div')
        box.className = 'exo-phone-verify'
        box.hidden = true
        box.innerHTML =
            '<div class="exo-phone-verify__digits">' +
            '<input type="tel" inputmode="numeric" maxlength="1" class="exo-phone-verify__digit">'.repeat(DIGITS) +
            '</div>' +
            '<div class="exo-phone-verify__actions">' +
            '<button type="button" class="exo-phone-verify__paste">' + CLIPBOARD_ICON + '<span></span></button>' +
            '<div class="exo-phone-verify__resend">' +
            '<button type="button" class="exo-phone-verify__request"></button>' +
            '<span class="exo-phone-verify__counter" hidden></span>' +
            '</div>' +
            '</div>' +
            '<small class="exo-phone-verify__msg" aria-live="polite"></small>'
        const q = sel => box.querySelector(sel)
        const digits = Array.from(box.querySelectorAll('.exo-phone-verify__digit'))
        const pasteBtn = q('.exo-phone-verify__paste')
        const requestBtn = q('.exo-phone-verify__request')
        const counter = q('.exo-phone-verify__counter')
        const msg = q('.exo-phone-verify__msg')
        pasteBtn.querySelector('span').textContent = L.paste || 'Paste the code'
        requestBtn.textContent = L.request || 'Request new code'
        digits[0].autocomplete = 'one-time-code'

        const wrap = input.closest('.iti') || input.parentNode
        wrap.after(box)
        const ok = document.createElement('span')
        ok.className = 'exo-phone-verify__ok'
        ok.hidden = true
        ok.setAttribute('role', 'img')
        ok.setAttribute('aria-label', L.verified || 'Verified')
        ok.innerHTML = CHECK_ICON
        wrap.appendChild(ok)

        // "Send code" sits inside the field, at its end, the moment the number is
        // valid: the visitor has just typed it and the next step is right there.
        const sendBtn = document.createElement('button')
        sendBtn.type = 'button'
        sendBtn.className = 'exo-phone-verify__send'
        sendBtn.hidden = true
        sendBtn.textContent = L.send || 'Send code'
        wrap.appendChild(sendBtn)

        // In the field's wrapper too, so it holds the submit before the code part
        // below is even shown.
        const gate = document.createElement('input')
        gate.className = 'exo-phone-verify__gate'
        gate.tabIndex = -1
        gate.setAttribute('aria-hidden', 'true')
        wrap.appendChild(gate)

        const verified = new Set(v.verifiedPhone ? [v.verifiedPhone] : [])
        const inputRequired = input.required
        let sentFor = null   // the number the visible code was sent to
        let busy = false
        let timer = null

        // Re-run ExoForm's submit gate (it listens for input/change on the form).
        const notify = () => gate.dispatchEvent(new Event('change', { bubbles: true }))
        const say = (text, isError) => {
            msg.textContent = text || ''
            msg.classList.toggle('exo-phone-verify__msg--error', !!isError)
        }
        const clearDigits = () => digits.forEach(d => { d.value = '' })

        const update = () => {
            const value = input.value.trim()
            const e164 = value ? iti.getNumber() : ''
            const valid = !!value && iti.isValidNumber() === true
            const done = valid && verified.has(e164)

            // A typed but impossible number holds the submit too.
            input.required = inputRequired || !!value
            input.setCustomValidity(value && !valid ? (L.invalid || 'Invalid') : '')

            if (e164 !== sentFor && sentFor !== null) {
                // The number changed after a code went out: back to "Send code".
                sentFor = null
                clearInterval(timer)
                clearDigits()
                say('')
            }

            const offer = valid && !done && sentFor === null
            box.hidden = !valid || done || sentFor === null
            sendBtn.hidden = !offer
            ok.hidden = !done
            wrap.classList.toggle('exo-phone--verified', done)
            wrap.classList.toggle('exo-phone--send', offer)
            gate.required = valid && !done
            gate.setCustomValidity(valid && !done ? (L.needed || 'Verify') : '')
            // The gate only re-checks on input/change, and this widget mounts after
            // ExoForm has run its first check — without this the button starts
            // enabled for a valid but unverified number.
            notify()
        }

        const countdown = (seconds) => {
            clearInterval(timer)
            let left = seconds
            const tick = () => {
                if (left <= 0) {
                    clearInterval(timer)
                    counter.hidden = true
                    requestBtn.hidden = false
                    return
                }
                counter.textContent = (L.requestWait || 'You can request a new code after :s seconds').replace(':s', left--)
                counter.hidden = false
                requestBtn.hidden = true
            }
            tick()
            timer = setInterval(tick, 1000)
        }

        const send = () => {
            if (busy) return
            busy = true
            sendBtn.disabled = true
            const phone = iti.getNumber()
            post('/_sys/forms/phone-code/send', { form_id: v.formId, field: v.field, phone })
                .then(({ ok: sent, data }) => {
                    if (data.verified) {
                        verified.add(data.phone)
                        update()
                        return
                    }
                    // A cooldown answer means a code for this number is already on its
                    // way (e.g. after a reload): show the boxes for it all the same.
                    if (sent || data.retry_after) {
                        sentFor = phone
                        clearDigits()
                        update()
                        countdown(data.retry_after || 60)
                        digits[0].focus()
                    }
                    // Success says nothing: the boxes appearing is the message.
                    say(sent ? '' : (data.message || L.failed), !sent)
                })
                .catch(() => say(L.failed, true))
                .then(() => { busy = false; sendBtn.disabled = false })
        }

        const check = () => {
            const code = digits.map(d => d.value).join('')
            if (code.length !== DIGITS || busy) return
            busy = true
            const phone = iti.getNumber()
            post('/_sys/forms/phone-code/verify', { phone, code })
                .then(({ ok: right, data }) => {
                    if (right) {
                        verified.add(phone)
                        clearInterval(timer)
                        say('')
                        update()
                    } else {
                        say(data.message || L.failed, true)
                        clearDigits()
                        digits[0].focus()
                    }
                })
                .catch(() => say(L.failed, true))
                .then(() => { busy = false })
        }

        // Spread pasted or autofilled digits over the boxes, from the first one.
        const spread = (text) => {
            const found = String(text || '').replace(/\D/g, '').slice(0, DIGITS)
            digits.forEach((d, i) => { d.value = found[i] || '' })
            digits[Math.min(found.length, DIGITS - 1)].focus()
            check()
        }

        // Box behaviour follows the site's own e-mail code screen: a digit moves on,
        // backspace on an empty box moves back, arrows move, a paste spreads.
        digits.forEach((d, i) => {
            d.addEventListener('input', () => {
                const typed = d.value.replace(/\D/g, '')
                if (typed.length > 1) return spread(typed)
                d.value = typed
                if (typed && digits[i + 1]) digits[i + 1].focus()
                check()
            })
            d.addEventListener('keydown', (e) => {
                if (e.key === 'Backspace' && d.value === '' && digits[i - 1]) {
                    digits[i - 1].value = ''
                    digits[i - 1].focus()
                    e.preventDefault()
                } else if (e.key === 'ArrowLeft' && digits[i - 1]) {
                    digits[i - 1].focus()
                    e.preventDefault()
                } else if (e.key === 'ArrowRight' && digits[i + 1]) {
                    digits[i + 1].focus()
                    e.preventDefault()
                }
            })
        })
        box.querySelector('.exo-phone-verify__digits').addEventListener('paste', (e) => {
            e.preventDefault()
            spread((e.clipboardData || window.clipboardData).getData('text'))
        })
        pasteBtn.addEventListener('click', () => {
            if (!navigator.clipboard || !navigator.clipboard.readText) return
            navigator.clipboard.readText().then(spread).catch(() => {})
        })
        sendBtn.addEventListener('click', send)
        requestBtn.addEventListener('click', send)

        input.addEventListener('input', update)
        input.addEventListener('countrychange', update)
        update()
    },
}

// The e-mail code screen's clipboard icon, so both screens read the same.
const CLIPBOARD_ICON = '<svg width="14" height="17" viewBox="0 0 17 21" fill="none" aria-hidden="true"><path fill="currentColor" d="M0 18.2783C0 20.0132 0.85498 20.8848 2.57324 20.8848H10.376C12.0942 20.8848 12.9492 20.0049 12.9492 18.2783V16.7427H14.4019C16.1118 16.7427 16.9751 15.8628 16.9751 14.1362V7.396C16.9751 6.375 16.7676 5.72754 16.145 5.08838L11.9531 0.830078C11.3638 0.224121 10.6665 0 9.77832 0H6.59912C4.88916 0 4.02588 0.879883 4.02588 2.60645V4.14209H2.57324C0.863281 4.14209 0 5.01367 0 6.74854V18.2783ZM12.1606 9.5874L7.58691 4.93066C6.95605 4.2832 6.43311 4.14209 5.50342 4.14209H5.3623V2.63135C5.3623 1.80957 5.80225 1.33643 6.66553 1.33643H10.3345V5.59473C10.3345 6.55762 10.7993 7.01416 11.7539 7.01416H15.6387V14.1113C15.6387 14.9414 15.1904 15.4062 14.3271 15.4062H12.9492V11.7954C12.9492 10.7329 12.8247 10.2681 12.1606 9.5874ZM11.5381 5.39551V1.99219L15.29 5.81055H11.9448C11.6543 5.81055 11.5381 5.68604 11.5381 5.39551ZM1.33643 18.2534V6.76514C1.33643 5.95166 1.77637 5.47852 2.63965 5.47852H5.3623V10.293C5.3623 11.3389 5.89355 11.8618 6.92285 11.8618H11.6128V18.2534C11.6128 19.0835 11.1646 19.5483 10.3096 19.5483H2.63135C1.77637 19.5483 1.33643 19.0835 1.33643 18.2534ZM7.08057 10.6084C6.74854 10.6084 6.61572 10.4756 6.61572 10.1436V5.78564L11.3555 10.6084H7.08057Z"/></svg>'

const CHECK_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'

function post(url, body) {
    return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
    }).then(r => r.json().catch(() => ({})).then(data => ({ ok: r.ok && data.ok !== false, data })))
}
